import React, { useState } from 'react';
import {
    IonContent, IonSpinner, IonText, IonButton, IonIcon,
    IonToggle, IonBadge,
} from '@ionic/react';
import { useIonViewWillEnter } from '@ionic/react';
import {
    personAddOutline, createOutline, keyOutline, shieldCheckmarkOutline, settingsOutline,
} from 'ionicons/icons';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import permissionService from '@/services/PermissionService';
import lookupDataService from '@/services/data/LookupDataService';
import { UserManagementUserResponse } from '@/types/response/userManagementResponses';
import { SitesResponse, UserTypesResponse, GroupsResponse, ExpertiseAreasResponse } from '@/types/response/lookupResponses';
import AddUserModal from '@/components/admin/user-management/AddUserModal';
import UserDetailsModal from '@/components/admin/user-management/UserDetailsModal';
import ResetPasswordModal from '@/components/admin/user-management/ResetPasswordModal';
import SettingsModal from '@/components/common/SettingsModal';
import * as uma from '@/services/util/adapters/userManagementAdapters';

const UserManagement: React.FC = () => {
    usePageTitle('User Management');
    const [users, setUsers] = useState<UserManagementUserResponse[]>([]);
    const [sites, setSites] = useState<SitesResponse[]>([]);
    const [userTypes, setUserTypes] = useState<UserTypesResponse[]>([]);
    const [groups, setGroups] = useState<GroupsResponse[]>([]);
    const [areas, setAreas] = useState<ExpertiseAreasResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);

    // Filters
    const [showActive, setShowActive] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const [showAssigned, setShowAssigned] = useState(true);
    const [showUnassigned, setShowUnassigned] = useState(true);

    // Modals
    const [showAddUser, setShowAddUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserManagementUserResponse | null>(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showPrivileges, setShowPrivileges] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);

    const isEnterpriseAdmin = permissionService.isEnterpriseAdmin();
    const isModuleAdmin = permissionService.isModuleAdmin();
    const isSuperAdmin = permissionService.isSuperAdmin();

    const canFullEditUser = (u: UserManagementUserResponse) => {
        if (u.IsSuperAdmin) return isSuperAdmin;
        return isEnterpriseAdmin;
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [userList, siteList, typeList] = await Promise.all([
                apiService.getManagementUsers(),
                apiService.getContractSites(),
                apiService.getUserTypes(),
            ]);
            setUsers(userList);
            setSites(siteList);
            setUserTypes(typeList);

            const cachedGroups = lookupDataService.getGroups();
            setGroups(cachedGroups ? Array.from(cachedGroups.values()) : []);

            const cachedAreas = lookupDataService.getExpertiseAreas();
            setAreas(cachedAreas ? Array.from(cachedAreas.values()) : []);
        } catch {
            setToast({ message: 'Failed to load user management data.', variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useIonViewWillEnter(() => {
        loadData();
    });

    const openModal = (user: UserManagementUserResponse, modal: 'details' | 'privileges' | 'resetPassword' | 'preferences') => {
        setSelectedUser(user);
        if (modal === 'details') setShowDetails(true);
        else if (modal === 'privileges') setShowPrivileges(true);
        else if (modal === 'resetPassword') setShowResetPassword(true);
        else if (modal === 'preferences') setShowPreferences(true);
    };

    const filteredUsers = users.filter(u => {
        if (!showActive && u.IsActive) return false;
        if (!showInactive && !u.IsActive) return false;
        if (!showAssigned && u.AssignedToContract) return false;
        if (!showUnassigned && !u.AssignedToContract) return false;
        return true;
    });

    // Email regex — empty string falls back to basic validation in modals
    const emailRegex = '';

    return (
        <PageLayout>
            <IonContent>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                        <IonSpinner />
                    </div>
                ) : (
                    <div style={{ padding: '16px' }}>
                        {/* Toolbar */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', flex: 1 }}>
                                <IonToggle checked={showActive} onIonChange={e => setShowActive(e.detail.checked)} style={{ '--handle-width': '20px' }}>
                                    Active
                                </IonToggle>
                                <IonToggle checked={showInactive} onIonChange={e => setShowInactive(e.detail.checked)}>
                                    Inactive
                                </IonToggle>
                                <IonToggle checked={showAssigned} onIonChange={e => setShowAssigned(e.detail.checked)}>
                                    Assigned
                                </IonToggle>
                                <IonToggle checked={showUnassigned} onIonChange={e => setShowUnassigned(e.detail.checked)}>
                                    Unassigned
                                </IonToggle>
                            </div>
                            {isEnterpriseAdmin && (
                                <IonButton fill="solid" color="primary" onClick={() => setShowAddUser(true)}>
                                    <IonIcon icon={personAddOutline} slot="start" /> Add User
                                </IonButton>
                            )}
                        </div>

                        {/* User table */}
                        {filteredUsers.length === 0 ? (
                            <IonText color="medium" style={{ display: 'block', textAlign: 'center', padding: '3rem' }}>
                                No users match the current filters.
                            </IonText>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--ion-color-light-shade)', background: 'var(--ion-color-light)' }}>
                                            {['Name', 'User Name', 'User Type', 'Status', 'Assigned', 'Actions'].map(h => (
                                                <th key={h} style={{ textAlign: 'left', padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: 'var(--ion-color-medium)', fontWeight: '700', textTransform: 'uppercase' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map(user => (
                                            <tr key={user.Id} style={{ borderBottom: '1px solid var(--ion-color-light-shade)' }}>
                                                <td style={{ padding: '0.6rem 0.75rem' }}>
                                                    <div style={{ fontWeight: '500' }}>{user.FirstName} {user.LastName}</div>
                                                    {user.IsSuperAdmin && <IonBadge color="warning" style={{ fontSize: '0.65rem' }}>SuperAdmin</IonBadge>}
                                                    {user.IsEnterpriseAdmin && !user.IsSuperAdmin && <IonBadge color="primary" style={{ fontSize: '0.65rem' }}>EnterpriseAdmin</IonBadge>}
                                                </td>
                                                <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.9rem', color: 'var(--ion-color-medium)' }}>
                                                    {user.UserName}
                                                </td>
                                                <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.9rem' }}>
                                                    {user.UserTypeName ?? '—'}
                                                </td>
                                                <td style={{ padding: '0.6rem 0.75rem' }}>
                                                    <IonBadge color={user.IsActive ? 'success' : 'medium'}>
                                                        {user.IsActive ? 'Active' : 'Inactive'}
                                                    </IonBadge>
                                                </td>
                                                <td style={{ padding: '0.6rem 0.75rem' }}>
                                                    <IonBadge color={user.AssignedToContract ? 'primary' : 'light'}>
                                                        {user.AssignedToContract ? 'Assigned' : 'Unassigned'}
                                                    </IonBadge>
                                                </td>
                                                <td style={{ padding: '0.6rem 0.75rem' }}>
                                                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                        <IonButton fill="clear" size="small" title="Details"
                                                            onClick={() => openModal(user, 'details')}>
                                                            <IonIcon icon={createOutline} />
                                                        </IonButton>
                                                        {(isEnterpriseAdmin || isModuleAdmin) && (
                                                            <IonButton fill="clear" size="small" title="Privileges"
                                                                onClick={() => openModal(user, 'privileges')}>
                                                                <IonIcon icon={shieldCheckmarkOutline} />
                                                            </IonButton>
                                                        )}
                                                        {isEnterpriseAdmin && (
                                                            <IonButton fill="clear" size="small" title="Reset Password"
                                                                onClick={() => openModal(user, 'resetPassword')}>
                                                                <IonIcon icon={keyOutline} />
                                                            </IonButton>
                                                        )}
                                                        {(isEnterpriseAdmin || isModuleAdmin) && (
                                                            <IonButton fill="clear" size="small" title="Preferences"
                                                                onClick={() => openModal(user, 'preferences')}>
                                                                <IonIcon icon={settingsOutline} />
                                                            </IonButton>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                <AddUserModal
                    isOpen={showAddUser}
                    onDidDismiss={() => setShowAddUser(false)}
                    sites={sites}
                    userTypes={userTypes}
                    emailRegex={emailRegex}
                    onSaved={loadData}
                />

                <UserDetailsModal
                    isOpen={showDetails}
                    onDidDismiss={() => setShowDetails(false)}
                    user={selectedUser}
                    sites={sites}
                    userTypes={userTypes}
                    emailRegex={emailRegex}
                    canFullEdit={selectedUser ? canFullEditUser(selectedUser) : false}
                    onSaved={loadData}
                />

                <SettingsModal<uma.PrivilegesForm>
                    isOpen={showPrivileges}
                    onDidDismiss={() => setShowPrivileges(false)}
                    title={selectedUser ? `${selectedUser.FirstName} ${selectedUser.LastName} — Privileges` : 'User Privileges'}
                    fields={uma.privilegesFields}
                    emptyData={uma.privilegesEmptyForm}
                    initialData={uma.privilegesInitialData(selectedUser)}
                    onSave={uma.savePrivileges(selectedUser?.Id ?? 0)}
                    width="480px" height="620px"
                    successMessage="Privileges saved." errorMessage="Failed to save privileges."
                    onSaved={loadData}
                />

                <ResetPasswordModal
                    isOpen={showResetPassword}
                    onDidDismiss={() => setShowResetPassword(false)}
                    user={selectedUser}
                    onSaved={loadData}
                />

                <SettingsModal<uma.UserManagementPreferencesForm>
                    isOpen={showPreferences}
                    onDidDismiss={() => setShowPreferences(false)}
                    title={selectedUser ? `${selectedUser.FirstName} ${selectedUser.LastName} — Preferences` : 'User Preferences'}
                    fields={uma.userManagementPreferencesFields(groups, sites, areas)}
                    emptyData={uma.userManagementPreferencesEmptyForm}
                    initialData={uma.userManagementPreferencesInitialData(selectedUser)}
                    onSave={uma.saveUserManagementPreferences(selectedUser?.Id ?? 0)}
                    width="600px" height="80vh"
                    successMessage="Preferences saved." errorMessage="Failed to save preferences."
                    onSaved={loadData}
                />

                <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
            </IonContent>
        </PageLayout>
    );
};

export default UserManagement;
